import { Navigate } from "react-router-dom";
import { useShell } from "./useShell";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import type { ShellId } from "./types";
import { useEffect, type ReactNode } from "react";

interface ShellGuardProps {
  /** The shell this route tree requires. */
  requiredShell: ShellId;
  children: ReactNode;
}

/** Redirects to the user's default shell if they lack access to the required one.
 *  Waits for shell access to be resolved (bootstrap complete) before enforcing. */
export function ShellGuard({ requiredShell, children }: ShellGuardProps) {
  const { activeShell, shellAccess, isResolved, setActiveShell } = useShell();

  // Check if user has access to the required shell
  const hasAccess =
    (requiredShell === "admin" && shellAccess.global) ||
    (requiredShell === "ops" && shellAccess.ops) ||
    (requiredShell === "client" && shellAccess.client);

  // Links and direct URLs can cross planes without using the shell selector.
  // The authorized route owns the navigation; a denied route must not change it.
  useEffect(() => {
    if (isResolved && hasAccess && activeShell !== requiredShell) {
      setActiveShell(requiredShell);
    }
  }, [isResolved, hasAccess, activeShell, requiredShell, setActiveShell]);

  // Wait for bootstrap — prevents premature redirect while shellAccess is all-false
  if (!isResolved) {
    return <LoadingSpinner />;
  }

  if (!hasAccess) {
    // Redirect to the user's active (default) shell
    switch (activeShell) {
      case "admin":
        return <Navigate to="/admin" replace />;
      case "ops":
        return <Navigate to="/ops" replace />;
      case "client":
        return <Navigate to="/client" replace />;
    }
  }

  return <>{children}</>;
}
